import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value);
    const max = parseInt(quantityInput.max);
    
    // Check if we're already at max before trying to increase
    if (max && currentValue >= max) {
      // Show error message if we're on product page (not cart)
      if (!quantityInput.dataset.cartLine) {
        this.#showInventoryError(max);
      }
      return;
    }
    
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange(event);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity change event.
   * @param {Event} event - The event.
   */
  #onQuantityChange(event) {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (newValue < min && min) quantityInput.value = min;
    if (newValue > max && max) {
      quantityInput.value = max;
      // Show error message if we're on product page (not cart)
      if (!quantityInput.dataset.cartLine) {
        this.#showInventoryError(max);
      }
    }
  };

  /**
   * Shows inventory error message on product page
   * @param {number} maxQuantity - The maximum available quantity
   */
  #showInventoryError = (maxQuantity) => {
    // Find the product form component
    const productForm = this.closest('product-form-component');
    if (!productForm || !productForm.refs) return;

    const addToCartTextError = productForm.refs.addToCartTextError;
    if (!addToCartTextError) return;

    // Show the error container
    addToCartTextError.classList.remove('hidden');

    // Following the same pattern as product-form.js (lines 199-204)
    // Reuse the text node if it exists (childNodes[2] is after icon + text)
    const textNode = addToCartTextError.childNodes[2];
    if (textNode && textNode.nodeType === Node.TEXT_NODE) {
      textNode.textContent = ` Only ${maxQuantity} available in stock`;
    } else {
      const newTextNode = document.createTextNode(` Only ${maxQuantity} available in stock`);
      addToCartTextError.appendChild(newTextNode);
    }

    // Auto-hide after 3 seconds
    setTimeout(() => {
      addToCartTextError.classList.add('hidden');
    }, 3000);
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
